import React, { useEffect, useState } from 'react';
import { useAppSelector } from '../store';
import ThemeToggle from '../components/ThemeToggle';
import UserSettingsDropdown from '../components/UserSettingsDropdown';
import Toast from '../components/Toast';
import Icon from '../components/Icon';
import BankSidebar from './sidebars/BankSidebar';
import PsoSidebar from './sidebars/PsoSidebar';
import RegionalSidebar from './sidebars/RegionalSidebar';
import InchargeSidebar from './sidebars/InchargeSidebar';
import { SidebarProvider } from './SidebarContext';
import { FiMenu, FiX } from 'react-icons/fi';

type Props = { children: React.ReactNode };

export default function DashboardLayout({ children }: Props) {
    const bank = useAppSelector((s) => s.user.bank);
    const pso = useAppSelector((s) => s.user.pso);
    const [toast, setToast] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        const role = sessionStorage.getItem('role');
        if (role === 'bank') { setToast(null); sessionStorage.removeItem('toast'); return; }
        if (pso) { setToast(null); sessionStorage.removeItem('toast'); return; }
        const queued = sessionStorage.getItem('toast');
        if (queued) { setToast(queued); sessionStorage.removeItem('toast'); return; }
    }, [pso]);

    // Close sidebar when window is resized to desktop size
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isRegional = !!pso && /regional/i.test(pso.designation || '');
    const isIncharge = !!pso && /incharge/i.test(pso.designation || '');

    // Determine which sidebar to show
    const renderSidebar = () => {
        if (!pso) return <BankSidebar />;
        if (isIncharge) return <InchargeSidebar />;
        if (isRegional) return <RegionalSidebar />;
        return <PsoSidebar />;
    };

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50 text-gray-900 dark:bg-[#0f172a] dark:text-gray-100">
            <div className="pointer-events-none fixed right-4 top-4 z-50 space-y-2">
                {toast && (
                    <div className="pointer-events-auto">
                        <Toast message={toast} type="warning" onClose={() => setToast(null)} />
                    </div>
                )}
            </div>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar - Drawer on mobile, fixed on desktop */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 h-screen w-64 transform border-r border-gray-200 bg-white transition-transform duration-300 ease-in-out dark:border-gray-700 dark:bg-black/30 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                {/* Close button for mobile */}
                <div className="flex items-center justify-between border-b border-gray-200 p-4 lg:hidden dark:border-white/10">
                    <div className="text-sm font-semibold">
                        {!pso ? 'Bank Menu' : isIncharge ? 'Incharge Menu' : isRegional ? 'Regional Head' : 'PSO Menu'}
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                    >
                        <Icon icon={FiX} className="h-5 w-5" />
                    </button>
                </div>
                <SidebarProvider onClose={() => setSidebarOpen(false)}>
                    {renderSidebar()}
                </SidebarProvider>
            </aside>

            <div className="flex h-screen flex-1 flex-col overflow-hidden">
                <header className="shrink-0 border-b border-gray-200 bg-white/90 backdrop-blur dark:border-gray-700 dark:bg-black/40">
                    <div className="flex items-center justify-between px-6 py-3">
                        <div className="flex items-center gap-3">
                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
                            >
                                <Icon icon={FiMenu} className="h-5 w-5" />
                            </button>
                            <h1 className="text-lg font-semibold">Dashboard</h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right">
                                {pso ? (
                                    <>
                                        <div className="text-sm font-medium">{pso.name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{pso.designation || 'PSO'}</div>
                                    </>
                                ) : (
                                    <div className="text-sm font-semibold">Admin</div>
                                )}
                            </div>
                            <ThemeToggle />
                            <UserSettingsDropdown />
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-y-auto p-6">{children}</main>
            </div>
        </div>
    );
}


